// WPA2-PSK passphrase length limits enforced by the WiFi spec itself (and by
// CircuitPython's wifi.radio.start_ap()). The device firmware performs no
// validation before passing wifi_ap_password straight through, so an
// out-of-range value throws uncaught at boot and the access point never
// starts (see midulate/app/lib/midulate/wifi_manager.py _start_ap()).
export const WIFI_AP_PASSWORD_MIN_LENGTH = 8;
export const WIFI_AP_PASSWORD_MAX_LENGTH = 63;

// null/empty means an open (unsecured) network, which is always valid.
export function isValidApPassword(password) {
  if (!password) return true;
  return (
    password.length >= WIFI_AP_PASSWORD_MIN_LENGTH &&
    password.length <= WIFI_AP_PASSWORD_MAX_LENGTH
  );
}

// The passphrase every unit ships with, set in the firmware repo's factory
// config (script/provisioning/config.default.json) — MDL-361.
//
// The firmware knows nothing about this value: `null` still means "open"
// there, so devices already in the field are not locked out by an update
// demanding a passphrase their owner never saw. A unit is secure because the
// config it was provisioned with says so.
//
// It is a published default, so it stops someone casually joining but gives
// no real confidentiality against anyone who looks it up — WPA2-PSK derives
// the session key from the passphrase, so a known PSK plus a captured
// handshake is enough to decrypt. Treated as "not yet secured" in the UI for
// that reason.
export const DEFAULT_AP_PASSWORD = 'midulate';

/** True when the AP is still on the shipped default rather than the owner's own. */
export function isDefaultApPassword(password) {
  return (password ?? '').trim() === DEFAULT_AP_PASSWORD;
}

/**
 * The single answer to "how secure is broadcast mode?" (MDL-387).
 *
 * Returns one of:
 *   'invalid' — the passphrase is out of range. Not merely insecure: the
 *               sanitiser rejects it and `start_ap()` never runs, so the
 *               network does not come up at all. Outranks everything else,
 *               because disabling AP fallback does not make a bad passphrase
 *               saveable.
 *   'open'    — no passphrase and AP fallback enabled: an open network.
 *   'default' — still the passphrase every unit ships with. A published
 *               default stops someone casually joining but gives no real
 *               confidentiality, so it is not "secured" (MDL-361).
 *   'secure'  — a passphrase the owner chose, or AP fallback disabled.
 *
 * This exists because OnboardingWifiStep and OpenApWarning each derived the
 * same thing separately, with a comment promising they were "kept in sync
 * manually". They drifted: the warning grew a third state for the shipped
 * default and the onboarding step did not, so onboarding told owners their
 * device was secure while it was still on the factory passphrase.
 */
export function apSecurityState(settings) {
  const password = (settings?.wifi_ap_password ?? '').trim();

  if (!isValidApPassword(password)) return 'invalid';
  if (settings?.wifi_ap_disabled) return 'secure';
  if (!password) return 'open';
  if (isDefaultApPassword(password)) return 'default';
  return 'secure';
}
